import React, { useState, useEffect, useCallback } from 'react';
import {
  Facebook, RefreshCw, CheckCircle, XCircle, Loader2,
  Activity, Link2, Unlink, Copy, Bot, Users, Save
} from 'lucide-react';
import { API_URL } from '../../config';

// ============================================================
// ADMIN - Pages connectées via l'app Meta officielle (teckia-agent)
// Monitoring: tokens, webhooks, bots
// ============================================================

const authHeaders = () => ({
  'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
  'Content-Type': 'application/json'
});

export default function OfficialPagesManagement() {
  const [appInfo, setAppInfo] = useState(null);
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [healthLoading, setHealthLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [copied, setCopied] = useState(false);

  const loadPages = useCallback(async (withHealth = false) => {
    if (withHealth) setHealthLoading(true); else setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/facebook/admin/pages${withHealth ? '?health=1' : ''}`, {
        headers: authHeaders()
      });
      const data = await res.json();
      if (data.success) {
        setAppInfo(data.app);
        setPages(data.pages);
      }
    } catch (e) {
      console.error('Erreur chargement pages officielles:', e);
    } finally {
      setLoading(false);
      setHealthLoading(false);
    }
  }, []);

  useEffect(() => { loadPages(true); }, [loadPages]);

  const resubscribe = async (page) => {
    setBusyId(page.client_id);
    try {
      const res = await fetch(`${API_URL}/api/facebook/pages/${page.client_id}/resubscribe`, {
        method: 'POST', headers: authHeaders()
      });
      const data = await res.json();
      alert(data.success ? `✅ Webhook ré-abonné pour ${page.page_name}` : `❌ Échec: ${JSON.stringify(data.result)}`);
      loadPages(true);
    } finally {
      setBusyId(null);
    }
  };

  const disconnect = async (page) => {
    if (!window.confirm(`Déconnecter « ${page.page_name} » ?`)) return;
    setBusyId(page.client_id);
    try {
      await fetch(`${API_URL}/api/facebook/pages/${page.client_id}/disconnect`, {
        method: 'POST', headers: authHeaders()
      });
      loadPages(false);
    } finally {
      setBusyId(null);
    }
  };

  const copyWebhook = () => {
    navigator.clipboard.writeText(appInfo?.webhook_url || '');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const Badge = ({ ok, labelOk, labelKo }) => (
    ok === undefined ? <span className="text-gray-500 text-xs">—</span> :
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
      ok ? 'bg-green-500/20 text-green-300' : 'bg-red-500/20 text-red-300'
    }`}>
      {ok ? <CheckCircle className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
      {ok ? labelOk : labelKo}
    </span>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-10 h-10 text-blue-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-white mb-1 flex items-center gap-3">
            <Facebook className="w-8 h-8 text-blue-400" />
            Pages — API Officielle
          </h1>
          <p className="text-sm text-blue-200">
            Pages connectées via l'app Meta « teckia-agent » (OAuth, sans token manuel)
          </p>
        </div>
        <button
          onClick={() => loadPages(true)}
          disabled={healthLoading}
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-blue-500/20 text-blue-300 border border-blue-500/30 hover:bg-blue-500/30 transition-all text-sm font-medium disabled:opacity-50"
        >
          {healthLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Activity className="w-4 h-4" />}
          Vérifier santé (tokens + webhooks)
        </button>
      </div>

      {/* Info app */}
      {appInfo && (
        <div className="bg-white/5 border border-white/10 rounded-2xl p-4 md:p-5 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <div className="text-gray-400 text-xs mb-1">App Meta</div>
            <div className="text-white font-semibold text-sm">{appInfo.app_name}</div>
            <div className="text-gray-500 text-xs">ID: {appInfo.app_id}</div>
          </div>
          <div>
            <div className="text-gray-400 text-xs mb-1">URL Webhook (à mettre dans le dashboard Meta)</div>
            <button onClick={copyWebhook} className="flex items-center gap-2 text-blue-300 text-sm hover:text-blue-200">
              <span className="truncate max-w-[220px]">{appInfo.webhook_url}</span>
              <Copy className="w-3.5 h-3.5 flex-shrink-0" />
              {copied && <span className="text-green-400 text-xs">Copié !</span>}
            </button>
          </div>
          <div>
            <div className="text-gray-400 text-xs mb-1">Pages connectées</div>
            <div className="text-white font-bold text-2xl">{appInfo.total_pages}<span className="text-gray-500 text-sm font-normal"> / 500</span></div>
          </div>
        </div>
      )}

      {/* Table pages */}
      {pages.length === 0 ? (
        <div className="bg-white/5 border border-white/10 rounded-2xl p-10 text-center">
          <Link2 className="w-12 h-12 text-gray-500 mx-auto mb-3" />
          <p className="text-gray-400">Aucune page connectée via l'app officielle pour le moment.</p>
          <p className="text-gray-500 text-sm mt-1">Les clients se connectent depuis leur espace → « Page Facebook ».</p>
        </div>
      ) : (
        <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/10 text-left">
                  <th className="p-4 text-gray-400 font-medium">Page</th>
                  <th className="p-4 text-gray-400 font-medium">Statut</th>
                  <th className="p-4 text-gray-400 font-medium">Bot IA</th>
                  <th className="p-4 text-gray-400 font-medium">Token</th>
                  <th className="p-4 text-gray-400 font-medium">Webhook</th>
                  <th className="p-4 text-gray-400 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {pages.map(page => (
                  <tr key={page.client_id} className="border-b border-white/5 hover:bg-white/5">
                    <td className="p-4">
                      <div className="text-white font-medium">{page.page_name}</div>
                      <div className="text-gray-500 text-xs">{page.page_id}</div>
                    </td>
                    <td className="p-4">
                      <Badge ok={page.status === 'active'} labelOk="Active" labelKo={page.status} />
                    </td>
                    <td className="p-4">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs ${
                        page.bot_active ? 'bg-green-500/20 text-green-300' : 'bg-gray-500/20 text-gray-400'
                      }`}>
                        <Bot className="w-3 h-3" />
                        {page.bot_active ? 'Actif' : 'Pause'}
                      </span>
                    </td>
                    <td className="p-4"><Badge ok={page.token_valid} labelOk="Valide" labelKo="Invalide" /></td>
                    <td className="p-4"><Badge ok={page.webhook_subscribed} labelOk="Abonné" labelKo="Non abonné" /></td>
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => resubscribe(page)}
                          disabled={busyId === page.client_id}
                          title="Ré-abonner le webhook"
                          className="p-2 rounded-lg bg-blue-500/20 text-blue-300 hover:bg-blue-500/30 disabled:opacity-50"
                        >
                          <RefreshCw className={`w-4 h-4 ${busyId === page.client_id ? 'animate-spin' : ''}`} />
                        </button>
                        <button
                          onClick={() => disconnect(page)}
                          disabled={busyId === page.client_id}
                          title="Déconnecter la page"
                          className="p-2 rounded-lg bg-red-500/20 text-red-300 hover:bg-red-500/30 disabled:opacity-50"
                        >
                          <Unlink className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Limites de pages par client (facturation par page) */}
      <PageLimitsSection />
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// Limites de pages par client — 1 page = 1 abonnement facturé
// ════════════════════════════════════════════════════════════
function PageLimitsSection() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edits, setEdits] = useState({});
  const [savingId, setSavingId] = useState(null);
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/bot/page-limits`, { headers: authHeaders() });
      const data = await res.json();
      if (data.success) setUsers(data.users);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveLimit = async (userId) => {
    const value = edits[userId];
    if (value === undefined) return;
    setSavingId(userId);
    try {
      const res = await fetch(`${API_URL}/api/bot/page-limits/${userId}`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ max_pages: parseInt(value, 10) || 0 })
      });
      const data = await res.json();
      if (data.success) {
        setUsers(prev => prev.map(u => u.user_id === userId ? { ...u, max_pages: data.max_pages } : u));
        setEdits(prev => { const n = { ...prev }; delete n[userId]; return n; });
      }
    } finally { setSavingId(null); }
  };

  const filtered = users.filter(u =>
    !search || (u.email + u.name).toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="bg-white/5 border border-white/10 rounded-2xl p-4 md:p-5">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-purple-600/30 flex items-center justify-center">
            <Users className="w-4 h-4 text-purple-300" />
          </div>
          <div>
            <div className="text-white font-semibold text-sm">Limites de pages par client</div>
            <div className="text-gray-500 text-xs">1 page = 1 abonnement facturé — le client ne peut pas dépasser sa limite</div>
          </div>
        </div>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Rechercher un client..."
          className="bg-white/10 border border-white/20 rounded-xl px-3 py-2 text-white text-sm placeholder-gray-500 w-full md:w-64"
        />
      </div>

      {loading ? (
        <div className="py-6 text-center"><Loader2 className="w-6 h-6 text-blue-400 animate-spin mx-auto" /></div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/10 text-left">
                <th className="p-3 text-gray-400 font-medium">Client</th>
                <th className="p-3 text-gray-400 font-medium">Pages utilisées</th>
                <th className="p-3 text-gray-400 font-medium">Limite autorisée</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.slice(0, 30).map(u => {
                const overLimit = u.pages_used > u.max_pages;
                return (
                  <tr key={u.user_id} className="border-b border-white/5 hover:bg-white/5">
                    <td className="p-3">
                      <div className="text-white text-sm">{u.name || '—'}</div>
                      <div className="text-gray-500 text-xs">{u.email}</div>
                    </td>
                    <td className="p-3">
                      <span className={`font-bold ${overLimit ? 'text-red-400' : u.pages_used > 0 ? 'text-green-300' : 'text-gray-500'}`}>
                        {u.pages_used}
                      </span>
                      {overLimit && <span className="text-red-400 text-xs ml-2">⚠ dépasse la limite</span>}
                    </td>
                    <td className="p-3">
                      <input
                        type="number" min="0" max="1000"
                        value={edits[u.user_id] !== undefined ? edits[u.user_id] : u.max_pages}
                        onChange={(e) => setEdits(prev => ({ ...prev, [u.user_id]: e.target.value }))}
                        className="w-20 bg-white/10 border border-white/20 rounded-lg px-3 py-1.5 text-white text-sm"
                      />
                    </td>
                    <td className="p-3">
                      {edits[u.user_id] !== undefined && (
                        <button
                          onClick={() => saveLimit(u.user_id)}
                          disabled={savingId === u.user_id}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-green-500/20 text-green-300 hover:bg-green-500/30 text-xs font-medium disabled:opacity-50"
                        >
                          {savingId === u.user_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                          Enregistrer
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {filtered.length > 30 && (
            <p className="text-gray-500 text-xs mt-2">30 premiers affichés — utilisez la recherche pour trouver un client.</p>
          )}
        </div>
      )}
    </div>
  );
}
